import { useEffect, useState } from 'react';
import { Box, Portal, useStyleConfig } from '@chakra-ui/react';

// Room the tooltip needs before it flips to the other side of the pointer
const FLIP_MARGIN = { x: 320, y: 60 };

/**
 * A tooltip for a whole grid row, following the pointer, in the theme's
 * Tooltip style. `getLabel(rowData)` gives its text, or nothing for rows
 * without one. An element in the row with its own tooltip (e.g. a copy
 * button or status pill) takes over while that tooltip is open.
 */
const RowHoverTooltip = ({ containerRef, gridRef, getLabel }) => {
    const styles = useStyleConfig('Tooltip');
    const [tip, setTip] = useState(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !getLabel) return undefined;

        let pointer = null;
        const update = () => {
            if (!pointer) return;
            const { target, clientX, clientY } = pointer;
            const rowId = target.closest?.('.ag-row')?.getAttribute('row-id');
            const data =
                rowId == null
                    ? null
                    : gridRef.current?.api?.getRowNode(rowId)?.data;
            // Chakra marks an element whose tooltip is open this way
            const hasOwnTooltip = !!target.closest?.('[aria-describedby]');
            const label = data && !hasOwnTooltip ? getLabel(data) : null;
            setTip(label ? { label, x: clientX, y: clientY } : null);
        };
        const handleMove = (event) => {
            pointer = event;
            update();
        };
        const handleLeave = () => {
            pointer = null;
            setTip(null);
        };
        // An element's own tooltip opens a moment after the pointer arrives,
        // so re-check whenever one opens or closes
        const observer = new MutationObserver(update);

        container.addEventListener('mousemove', handleMove);
        container.addEventListener('mouseleave', handleLeave);
        observer.observe(container, {
            subtree: true,
            attributes: true,
            attributeFilter: ['aria-describedby'],
        });
        return () => {
            container.removeEventListener('mousemove', handleMove);
            container.removeEventListener('mouseleave', handleLeave);
            observer.disconnect();
        };
    }, [containerRef, gridRef, getLabel]);

    if (!tip) return null;

    // Keep it on screen near the right and bottom edges
    const flipX = tip.x > window.innerWidth - FLIP_MARGIN.x;
    const flipY = tip.y > window.innerHeight - FLIP_MARGIN.y;
    return (
        <Portal>
            <Box
                role="tooltip"
                __css={styles}
                position="fixed"
                pointerEvents="none"
                left={flipX ? undefined : `${tip.x + 12}px`}
                right={
                    flipX ? `${window.innerWidth - tip.x + 12}px` : undefined
                }
                top={flipY ? undefined : `${tip.y + 18}px`}
                bottom={
                    flipY ? `${window.innerHeight - tip.y + 8}px` : undefined
                }>
                {tip.label}
            </Box>
        </Portal>
    );
};

export default RowHoverTooltip;
